import { useLazyCreateCustomerQuery } from '@api/customer.api';
import { CreateCustomerRequest } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, Title, useDealerDetailsContext } from '@components';
import { useAppDispatch, useAppSelector, useToast } from '@hooks';
import { Close } from '@mui/icons-material';
import { Alert, Box, Modal, Stack, TextField, styled } from '@mui/material';
import { getDealer } from '@store/dealer/dealer.slice';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import { GlobalStyles } from '@themes/global/GlobalStyles';
import { t } from '@translations';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

const ModalContentWrapper = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 16px;
  background-color: #f5f5f5;
  border: 1px solid #8bb770;
  min-width: 400px;
  max-height: 80vh;
  overflow: auto;
`;

const StyledTextField = styled(TextField)({
  '& legend span': {
    display: 'none',
  },
});

export const CreateCustomerModal = () => {
  const { addUser, createModalId, toggleEditDataState } = useDealerDetailsContext();
  const modalState = useAppSelector(getModalState(createModalId));
  const dealer = useSelector(getDealer);
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateCustomerRequest>();

  const { toast } = useToast();

  const closeHandler = () => {
    reset();
    dispatch(setModalState({ id: createModalId, isVisible: false }));
  };

  const [trigger, status] = useLazyCreateCustomerQuery();
  const { isLoading, isFetching, error } = status;
  const isWorking = isLoading || isFetching;
  const errorMessage = (error as any)?.data?.data?.message;

  const onSubmit: SubmitHandler<CreateCustomerRequest> = async (formData) => {
    try {
      await trigger({ ...formData, organizationId: dealer.id as string }).unwrap();
      closeHandler();
      toggleEditDataState(false);
      addUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        userEmail: formData.email,
        userPhoneNumber: formData.phoneNumber,
        userName: `${formData.firstName} ${formData.lastName}`,
      });
      toast({
        severity: 'success',
        message: t(TextCodes.Dealer.Details.CreateUserSuccess),
        autoHideDuration: 5000,
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      open={modalState.isVisible}
      onClose={closeHandler}
      aria-labelledby='create-user-modal'
      id={createModalId}
    >
      <GlobalStyles>
        <ModalContentWrapper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title variant='h5'>{t(TextCodes.Dealer.Details.CreateUser)}</Title>
            <Close onClick={closeHandler} sx={{ cursor: 'pointer' }} />
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <br />
              <StyledTextField
                label={t(TextCodes.Dealer.Details.FirstName)}
                {...register('firstName', { required: true })}
                error={!!errors.firstName}
                helperText={
                  errors.firstName ? t(TextCodes.Base.GenericError.FirstNameRequired) : ''
                }
              />
              <br />
              <StyledTextField
                label={t(TextCodes.Dealer.Details.LastName)}
                {...register('lastName', { required: true })}
                error={!!errors.lastName}
                helperText={errors.lastName ? t(TextCodes.Base.GenericError.LastNameRequired) : ''}
              />
              <br />
              <StyledTextField
                label={t(TextCodes.Dealer.Details.Email)}
                {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
                error={!!errors.email}
                helperText={errors.email ? t(TextCodes.Base.GenericError.InvalidEmailAddress) : ''}
              />
              <br />
              <StyledTextField
                label={t(TextCodes.Dealer.Details.PhoneNumber)}
                {...register('phoneNumber', { required: true })}
                error={!!errors.phoneNumber}
                helperText={
                  errors.phoneNumber ? t(TextCodes.Base.GenericError.PhoneNumberRequired) : ''
                }
              />
              <br />
              {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
              <Button type='submit' busy={isWorking} disabled={isWorking}>
                {t(TextCodes.Dealer.Details.CreateUser)}
              </Button>
            </Stack>
          </form>
        </ModalContentWrapper>
      </GlobalStyles>
    </Modal>
  );
};
